import { format } from "date-fns";
import { Text } from "../../components";
import { OrderStatusChip } from "./orderStatusChip";
import type { TableColumn } from "../../components/Table";
import type { OrderItem } from "../../apis/order";
import { OrderStatus } from "../../apis/order";
import { Tools } from "../../utils";
import { BookingPartnerLocation } from "../../apis/products";

function AppointmentDateCell({ row }: { row: OrderItem }) {
  const timeZone = row.partnerLocation?.timeZone;
  if (!row.serviceDateTime) return <Text>{"--"}</Text>;

  function formatDate(iso: string) {
    const date = Tools.zonedDate(iso, timeZone);
    if (!date) return iso;
    return `${format(date, "dd/MM/yyyy, HH:mm")} (${timeZone})`;
  }

  const current = formatDate(row.serviceDateTime);
  const originalDateTime = row.originalBooking?.serviceDateTime;

  if (originalDateTime)
    return (
      <div className="grid gap-[2px]">
        <Text>{current}</Text>
        <Text className="line-through text-critical">
          {formatDate(originalDateTime)}
        </Text>
      </div>
    );
  if (row.order?.orderStatus === OrderStatus.cancelled)
    return <Text className="line-through text-critical">{current}</Text>;
  return <Text>{current}</Text>;
}

export function getManageBookingsColumns({
  t,
}: {
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return [
    {
      key: "serviceDateTime",
      title: t("manageBookings.columns.date"),
      className: "min-w-[300px]",
      classNames: { cell: "h-[70px]" },
      render: (_: string, row: OrderItem) => <AppointmentDateCell row={row} />,
    },
    {
      key: "order.code",
      title: t("manageBookings.columns.orderNumber"),
      className: "min-w-[120px]",
    },
    {
      key: "visitor",
      title: t("general.name"),
      className: "min-w-[180px]",
      render: (_: unknown, row: OrderItem) => (
        <Text>
          {`${row.visitorFirstName ?? ""} ${row.visitorLastName ?? ""}`.trim() ||
            "--"}
        </Text>
      ),
    },
    {
      key: "productName",
      title: t("manageBookings.columns.service"),
      className: "min-w-[180px]",
    },
    {
      key: "partnerLocation",
      title: t("manageBookings.columns.location"),
      className: "min-w-[160px]",
      render: (partnerLocation: BookingPartnerLocation) => (
        <Text>
          {partnerLocation?.code ?? partnerLocation?.city?.name ?? "--"}
        </Text>
      ),
    },
    {
      key: "insurancePolicyNumber",
      title: t("manageBookings.columns.policyCaseNumber"),
      className: "min-w-[160px]",
      render: (value: string) => <Text>{value?.trim() || "--"}</Text>,
    },
    {
      key: "insuranceProviderName",
      title: t("manageBookings.columns.insuranceProvider"),
      className: "min-w-[160px]",
      render: (value: string) => <Text>{value?.trim() || "--"}</Text>,
    },
    {
      key: "status",
      title: t("manageBookings.columns.status"),
      className: "min-w-[120px]",
      render: (_value: string, row: OrderItem) => (
        <OrderStatusChip status={row.unifiedStatus ?? row.status} />
      ),
    },
  ] as TableColumn<OrderItem>[];
}
